/**
 * AdminHelpConversationList - lista paginada de conversas do chat de dúvidas (aba Suporte).
 * Só apresentação: os dados, a página e a conversa aberta vêm de AdminHelpTab.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import AdminHelpLoadError from '@/components/admin/help/AdminHelpLoadError';
import type { HelpListPage, HelpListRow } from '@/services/adminHelpService';

interface AdminHelpConversationListProps {
  data: HelpListPage | null;
  loading: boolean;
  error: string | null;
  activeId: string | null;
  onRetry: () => void;
  onOpen: (id: string) => void;
  onPage: (page: number) => void;
}

const SECONDARY_BUTTON =
  'rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function personLabel(name: string | null, email: string | null): string | null {
  const parts = [name, email].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : null;
}

const AdminHelpConversationList: React.FC<AdminHelpConversationListProps> = ({
  data,
  loading,
  error,
  activeId,
  onRetry,
  onOpen,
  onPage,
}) => {
  const pages = data && data.size > 0 ? Math.max(1, Math.ceil(data.total / data.size)) : 1;

  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-border">
      <h3 className="border-b border-border p-4 text-sm font-semibold text-foreground">Conversas</h3>

      {error ? (
        <div className="p-4">
          <AdminHelpLoadError message={error} onRetry={onRetry} />
        </div>
      ) : !data ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Carregando conversas...</div>
      ) : data.rows.length === 0 ? (
        <div className={cn('p-6 text-center text-sm text-muted-foreground', loading && 'opacity-60')}>
          Nenhuma conversa no período
        </div>
      ) : (
        <ul className={cn('divide-y divide-border transition-opacity', loading && 'opacity-60')}>
          {data.rows.map((row) => (
            <li key={row.id}>
              <ConversationRow row={row} active={row.id === activeId} onOpen={onOpen} />
            </li>
          ))}
        </ul>
      )}

      {data && !error && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-sm">
          <span className="text-muted-foreground">
            página {data.page} de {pages} · total {data.total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onPage(data.page - 1)}
              disabled={loading || data.page <= 1}
              className={SECONDARY_BUTTON}
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => onPage(data.page + 1)}
              disabled={loading || data.page >= pages}
              className={SECONDARY_BUTTON}
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

function ConversationRow({ row, active, onOpen }: { row: HelpListRow; active: boolean; onOpen: (id: string) => void }) {
  const person = personLabel(row.display_name, row.email);
  return (
    <button
      type="button"
      onClick={() => onOpen(row.id)}
      className={cn('w-full px-4 py-3 text-left transition-colors hover:bg-muted/40', active && 'bg-muted/60')}
    >
      <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-foreground">{row.title}</div>
          <div className={cn('truncate text-xs text-muted-foreground', !person && 'italic')}>{person ?? 'conta apagada'}</div>
        </div>
        <div className="shrink-0 text-xs text-muted-foreground">{new Date(row.updated_at).toLocaleString('pt-BR')}</div>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {row.plan && <span className="rounded bg-muted px-1.5 py-0.5 text-foreground">{row.plan}</span>}
        <span>{plural(row.questions, 'pergunta', 'perguntas')}</span>
        {row.screenshots > 0 && <span>{plural(row.screenshots, 'print', 'prints')}</span>}
        {row.unknown && <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400">não soube</Badge>}
        {row.needs_team && <Badge className="bg-primary/10 text-primary">equipe</Badge>}
        {row.thumbs_down && <Badge className="bg-destructive/10 text-destructive">👎</Badge>}
      </div>
    </button>
  );
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return <span className={cn('rounded-full px-2 py-0.5 font-medium', className)}>{children}</span>;
}

export default AdminHelpConversationList;
